/**
 * THE ROUTER. Nothing else.
 *
 * This file stays a table of addresses on purpose. The architecture is explicit that Legacy's
 * single-file frontend — an `index.html` carrying 85,000 lines of uncommitted diff — is the thing
 * not to reproduce. Every screen lives in its own file and is reached by an address from
 * `routes.ts`, which is the only place a URL is constructed (09 §1.5).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ContextProvider } from './shell/ContextProvider';
import { AppShell } from './shell/AppShell';
import { SkeletonRows } from './components/StateBlock';
import { callOperation, sessionFromWhoami } from './api/operations';
import type { CompanySummary, SessionSummary } from './api/contract';
import { listOf, useServerData } from './api/useServerData';
import { SignInScreen } from './screens/SignInScreen';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { CompanyPickerScreen } from './screens/CompanyPickerScreen';
import { TenantHomeScreen } from './screens/TenantHomeScreen';
import { CompanyHomeScreen } from './screens/CompanyHomeScreen';
import { ModuleHomeScreen } from './screens/ModuleHomeScreen';
import { ParentScreenPlaceholder } from './screens/ParentScreenPlaceholder';
import { PlatformBillingScreen } from './screens/PlatformBillingScreen';
import { PlatformChargesScreen } from './screens/PlatformChargesScreen';
import { PlatformBillingRunScreen } from './screens/PlatformBillingRunScreen';
import { PlatformChangeRequestsScreen } from './screens/PlatformChangeRequestsScreen';
import { PlatformDunningScreen } from './screens/PlatformDunningScreen';
import { PlatformSupportQueueScreen } from './screens/PlatformSupportQueueScreen';
import { PlatformCatalogueScreen } from './screens/PlatformCatalogueScreen';
import { PlatformPriceBooksScreen } from './screens/PlatformPriceBooksScreen';
import { PlatformCommercialPolicyScreen } from './screens/PlatformCommercialPolicyScreen';
import { PlatformSignupDoorScreen } from './screens/PlatformSignupDoorScreen';
import { PlatformHealthScreen } from './screens/PlatformHealthScreen';
import { PlatformPriceBookScreen } from './screens/PlatformPriceBookScreen';
import { PlatformConsoleScreen } from './screens/PlatformConsoleScreen';
import { PlatformSecurityScreen } from './screens/PlatformSecurityScreen';
import { PlatformTenantsScreen } from './screens/PlatformTenantsScreen';
import { PlatformTenantScreen } from './screens/PlatformTenantScreen';
import { PlatformTenantProvisioningScreen } from './screens/PlatformTenantProvisioningScreen';
import { PlatformSubscriptionScreen } from './screens/PlatformSubscriptionScreen';
import { PlatformCommercialScreen } from './screens/PlatformCommercialScreen';
import { PlatformSupportGrantsScreen } from './screens/PlatformSupportGrantsScreen';
import { TenantAdminScreen } from './screens/TenantAdminScreen';
import { TenantOrganisationScreen } from './screens/TenantOrganisationScreen';
import { TenantPersonalisationScreen } from './screens/TenantPersonalisationScreen';
import { PublicSignupScreen } from './screens/PublicSignupScreen';
import { SignupVerifyScreen } from './screens/SignupVerifyScreen';
import { PaymentReturnScreen } from './screens/PaymentReturnScreen';
import { TenantReminderRulesScreen } from './screens/TenantReminderRulesScreen';
import { TenantWorkItemsScreen } from './screens/TenantWorkItemsScreen';
import { TenantUsersScreen } from './screens/TenantUsersScreen';
import { TenantUserInvitationScreen } from './screens/TenantUserInvitationScreen';
import { TenantUserScreen } from './screens/TenantUserScreen';
import { TenantCompaniesScreen } from './screens/TenantCompaniesScreen';
import { TenantCompanyOnboardingScreen } from './screens/TenantCompanyOnboardingScreen';
import { TenantCompanyScreen } from './screens/TenantCompanyScreen';
import { TenantRolesScreen } from './screens/TenantRolesScreen';
import { TenantRoleAuthoringScreen } from './screens/TenantRoleAuthoringScreen';
import { TenantCompanyAccessScreen } from './screens/TenantCompanyAccessScreen';
import { TenantSubscriptionScreen } from './screens/TenantSubscriptionScreen';
import { TenantBillingScreen } from './screens/TenantBillingScreen';
import { SourceConnectionsScreen } from './screens/SourceConnectionsScreen';
import { TenantEnquiriesScreen } from './screens/TenantEnquiriesScreen';
import { SourceConnectionScreen } from './screens/SourceConnectionScreen';
import { TenantSecurityScreen } from './screens/TenantSecurityScreen';
import { EmailChangeConfirmScreen } from './screens/EmailChangeConfirmScreen';
import { PersonalDetailsScreen } from './screens/PersonalDetailsScreen';
import { PersonalSecurityScreen } from './screens/PersonalSecurityScreen';
import { MfaEnrolmentScreen } from './screens/MfaEnrolmentScreen';
import { MfaChallengeScreen } from './screens/MfaChallengeScreen';
import { CredentialSetupScreen } from './screens/CredentialSetupScreen';
import { BlockedDocumentScreen } from './screens/BlockedDocumentScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { onUnauthenticated } from './api/client';
import {
  fixtureModules,
  fixturePeriods,
  fixturesEnabled,
} from './api/fixtures';
import { preSessionRoutePatterns, routePatterns, routes } from './routes';
import { landingFor, resolveLandingPath } from './shell/landing';

/** Route params are read here and handed to screens as plain props, so screens stay testable. */
function CompanyHomeRoute(): JSX.Element {
  const { companyUid } = useParams<{ companyUid: string }>();
  if (companyUid === undefined) return <NotFoundScreen />;
  return <CompanyHomeScreen companyUid={companyUid} />;
}

function ModuleHomeRoute(): JSX.Element {
  const { companyUid, moduleCode } = useParams<{ companyUid: string; moduleCode: string }>();
  if (companyUid === undefined || moduleCode === undefined) return <NotFoundScreen />;
  return <ModuleHomeScreen companyUid={companyUid} moduleCode={moduleCode} />;
}

function ParentScreenRoute(): JSX.Element {
  const { companyUid, moduleCode, parent } = useParams<{
    companyUid: string;
    moduleCode: string;
    parent: string;
  }>();
  if (companyUid === undefined || moduleCode === undefined || parent === undefined) {
    return <NotFoundScreen />;
  }
  return <ParentScreenPlaceholder companyUid={companyUid} moduleCode={moduleCode} parent={parent} />;
}

/** One person's card. The membership comes from the address — 09 s1.5, never from state. */
function TenantUserRoute(): JSX.Element {
  const { membershipUid } = useParams<{ membershipUid: string }>();
  if (membershipUid === undefined) return <NotFoundScreen />;
  return <TenantUserScreen membershipUid={membershipUid} />;
}

function PlatformTenantRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformTenantScreen tenantUid={tenantUid} />;
}

/** Finishing an existing customer. The uid comes from the address, not from component state. */
function PlatformTenantProvisioningResumeRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformTenantProvisioningScreen resumeTenantUid={tenantUid} />;
}

function PlatformSubscriptionRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformSubscriptionScreen tenantUid={tenantUid} />;
}

function PriceBookRoute(): JSX.Element {
  const { priceBookUid } = useParams<{ priceBookUid: string }>();
  if (priceBookUid === undefined) return <NotFoundScreen />;
  return <PlatformPriceBookScreen priceBookUid={priceBookUid} />;
}

function PlatformCommercialRoute(): JSX.Element {
  const { tenantUid } = useParams<{ tenantUid: string }>();
  if (tenantUid === undefined) return <NotFoundScreen />;
  return <PlatformCommercialScreen tenantUid={tenantUid} />;
}

function TenantCompanyRoute(): JSX.Element {
  const { companyUid } = useParams<{ companyUid: string }>();
  if (companyUid === undefined) return <NotFoundScreen />;
  return <TenantCompanyScreen companyUid={companyUid} />;
}

function SourceConnectionRoute(): JSX.Element {
  const { connectionUid } = useParams<{ connectionUid: string }>();
  if (connectionUid === undefined) return <NotFoundScreen />;
  return <SourceConnectionScreen connectionUid={connectionUid} />;
}

/**
 * The second factor's challenge reference travels in the ADDRESS, because there is no session yet.
 *
 * Read here rather than in the screen, so the screen stays renderable without a router — the same
 * reason `CompanyHomeRoute` and the others exist.
 */
function MfaChallengeRoute(): JSX.Element {
  const [params] = useSearchParams();
  return <MfaChallengeScreen challengeRef={params.get('challenge')} />;
}

/**
 * The SAME READ, for the enrolment door, which did not have one.
 *
 * `onMfaEnrolmentRequired` navigates here with `?challenge=`, and the route rendered
 * `<MfaEnrolmentScreen />` directly -- so the reference was written into the address and then
 * dropped, two lines below the route that reads the identical parameter correctly. The screen
 * could present no proof of the first factor and enrolment refused; see its `begin()`.
 */
function MfaEnrolmentRoute(): JSX.Element {
  const [params] = useSearchParams();
  return <MfaEnrolmentScreen signInChallengeRef={params.get('challenge')} />;
}

/**
 * The two mailed-token addresses. `kind` is stated by the ROUTE, never sniffed from the token:
 * the two acts have different consequences and a token that resolved to the wrong one would be
 * creating an account where somebody meant to reset a password.
 *
 * ── THE TOKEN IS READ FROM THE PATH, NOT FROM THE QUERY. **X8, AMEND-3 §C-4.** ─────────────────
 *
 * These two read `params.get('token')` — a QUERY parameter — while the producer
 * (`@glpoint/mail`'s `buildLink`) has always appended the token as the LAST PATH SEGMENT, and the
 * ruling keeps the segment because SEC9 forbids a one-time credential in a query string. So the
 * carrier moves here: `routePatterns` now ends in `/:token` and `useParams` reads it.
 *
 * `?? null` is deliberate and is not a formality. `useParams` answers `undefined` for a segment the
 * pattern did not capture, and `CredentialSetupScreen` already renders its own written "no link"
 * state for `null` (`CredentialSetupScreen.tsx:132-135`) — so somebody arriving at `/invitation`
 * with no token at all gets that screen rather than a crash or, worse, a redemption attempt with the
 * string `"undefined"`.
 */
function InvitationAcceptRoute(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  return <CredentialSetupScreen kind="invitation" token={token ?? null} />;
}

function PasswordResetRoute(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  return <CredentialSetupScreen kind="password_reset" token={token ?? null} />;
}

/**
 * The token rides as the LAST PATH SEGMENT (X8), so it is read the same way the other two mailed
 * addresses read theirs. `undefined` becomes `null`, which the screen states as *no link in the
 * address* rather than as a failed verification -- somebody reached `/verify-email` by hand and
 * nothing has been attempted.
 */
/**
 * 0032. The registration link's token is the last PATH SEGMENT, which is what `buildLink` appends
 * -- never `?token=`, for the reason `routes.ts` states about every mailed address.
 */
function SignupVerifyFromPath(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  return <SignupVerifyScreen token={token ?? null} />;
}

/**
 * 0041. The clearing provider's return address. The last segment is OUR reference for the payment
 * page -- see `routes.ts` -- and the screen does nothing with it but hand it to the server.
 */
function PaymentReturnFromPath(): JSX.Element {
  const { reference } = useParams<{ reference: string }>();
  return <PaymentReturnScreen reference={reference ?? null} />;
}

function EmailChangeConfirmFromPath(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  return <EmailChangeConfirmScreen token={token ?? null} />;
}

function BlockedDocumentRoute(): JSX.Element {
  const { companyUid, documentUid } = useParams<{ companyUid: string; documentUid: string }>();
  if (companyUid === undefined || documentUid === undefined) return <NotFoundScreen />;
  return <BlockedDocumentScreen companyUid={companyUid} documentUid={documentUid} />;
}

/**
 * The read a surface with no company list performs: none.
 *
 * Module level so it is REFERENTIALLY STABLE. `useServerData` re-runs whenever its thunk changes, so
 * an inline `() => Promise.resolve(...)` here would re-fire on every render — forever, on the
 * platform surface, which is the one surface that should be asking for nothing.
 */
const NO_COMPANIES = (): Promise<{ readonly companies: readonly CompanySummary[] }> =>
  Promise.resolve({ companies: [] });

/**
 * `/` for a session whose surface the server has stated.
 *
 * Three states, and they are three: the answer has not arrived (wait -- a redirect now would be a
 * guess), it arrived and named no surface (sign-in; `onUnauthenticated` handles the 401 case and
 * this covers a context the client does not recognise), or it named one (go there).
 */
function RootLanding({
  session,
  read,
}: {
  readonly session: SessionSummary | null;
  readonly read: boolean;
}): JSX.Element {
  if (!read) {
    return (
      <AppShell titleHe="GL-POINT">
        <SkeletonRows rows={3} />
      </AppShell>
    );
  }
  /*
   * NO SESSION ⇒ THE PUBLIC PAGE. **Section 8, 2026-10-27.** A visitor who has never had an account
   * was sent straight to a sign-in form, which is a door with no building attached to it. `/signin`
   * is unchanged and still the address the form lives at; this is what `/` now answers.
   */
  if (session === null) return <Navigate to={routes.welcome} replace />;
  return <Navigate to={landingFor(session.sessionContext)} replace />;
}

export function App(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * IS THIS ADDRESS ONE THAT IS REACHED BEFORE A SESSION EXISTS? **The fix for the 2026-10-15
   * eviction.**
   *
   * `preSessionRoutePatterns` names them, and `matchPath` decides membership by the ROUTER'S
   * own matcher rather than by a string test: `/security` is the signed-in account screen and a
   * prefix of `/security/mfa/enrolment`, and `/signin` is a prefix of `/signin/reset/<token>`, so
   * `startsWith` would get two of the five wrong in opposite directions.
   */
  const preSession = preSessionRoutePatterns.some(
    (pattern) => matchPath(pattern, location.pathname) !== null,
  );

  /*
   * 401 ⇒ sign-in. The client raises it; the router acts on it. Neither decides *why*.
   *
   * -- EXCEPT ON AN ADDRESS THAT HAS NO SESSION BY DESIGN, WHICH IS WHAT WENT WRONG -------------
   *
   * The MFA arms of `auth.session.create` set NO COOKIE -- a challenge is not a session -- and the
   * mail links are followed by people who have no account yet. So `/api/me` answers 401 on all
   * of these addresses, CORRECTLY, and this handler then sent the browser to `/signin`: the
   * sign-in screen navigated to the enrolment door, the address change re-fired the session read
   * below, its 401 arrived here, and the person was returned to `/signin` with nothing to show
   * them, because nothing had failed. Reproduced in `screens/signInTransition.dom.test.tsx`.
   *
   * READ THROUGH A REF, not captured. This effect must not re-register on every address change --
   * `onUnauthenticated` keeps ONE handler, so a second registration is a silent replacement -- but
   * the answer it needs is about the address the person is on WHEN THE 401 ARRIVES, which may be a
   * later one than when the handler was installed. A captured `preSession` would be the stale
   * answer in exactly the race this fix exists to close.
   */
  const preSessionNow = useRef(preSession);
  preSessionNow.current = preSession;

  useEffect(() => {
    onUnauthenticated(() => {
      if (preSessionNow.current) return;
      navigate(routes.signIn);
    });
  }, [navigate]);

  const go = useCallback((path: string) => navigate(path), [navigate]);

  /*
   * THE SESSION IS READ FROM THE SERVER. **CM-030, and the fix for CM-014.**
   *
   * `auth.whoami.read` was declared, classified reachable, and called by nothing — so this line used
   * to mint a session from `location.pathname`, and typing `/platform` granted the platform surface.
   * The route is now called on mount and on every address change, which is also what makes a cold
   * load work: the surface is re-read rather than remembered, so there is nothing for a plain
   * `<a href>` navigation to discard.
   *
   * A 401 is NOT handled here. `onUnauthenticated` above already routes to sign-in, and a second
   * decision about what an unauthenticated read means is a second place for the two to disagree.
   */
  const [session, setSession] = useState<SessionSummary | null>(null);
  /*
   * WHETHER THE QUESTION HAS BEEN ANSWERED, which is not the same fact as the answer.
   *
   * `session === null` meant two opposite things at once: "the server has not told us yet" and
   * "the server told us nothing usable". The root redirect has to tell them apart -- redirecting
   * on the first is guessing a surface before anyone stated one, which is CM-014 with better
   * timing -- so the read now records that it completed, separately from what it returned.
   */
  const [sessionRead, setSessionRead] = useState(false);

  useEffect(() => {
    /*
     * A PRE-SESSION ADDRESS IS NOT ASKED WHO IT IS.
     *
     * The question has exactly one honest answer on these five -- nobody -- and asking it cost a
     * guaranteed 401 on every arrival at the sign-in screen, which is the 401 that evicted the
     * person from the enrolment door. The read is skipped and the state set to what the answer
     * would have been, so `sessionRead` still records that the question is SETTLED: leaving it
     * false would hang `RootLanding` on its skeleton, and that distinction is the one CM-014
     * turns on.
     */
    if (preSession) {
      setSession(null);
      setSessionRead(true);
      return;
    }

    let current = true;
    void callOperation('auth.whoami.read', {}).then(
      (me) => {
        if (current) {
          setSession(sessionFromWhoami(me));
          setSessionRead(true);
        }
      },
      () => {
        // Left null. There is no fallback surface: a session the server did not state is not a
        // session, and guessing one is the defect CM-014 records.
        if (current) {
          setSession(null);
          setSessionRead(true);
        }
      },
    );
    return () => {
      current = false;
    };
  }, [location.pathname, preSession]);

  /*
   * ── THE SAME READ, ON DEMAND. **2026-10-26.** ───────────────────────────────────────────────
   *
   * A screen that changes something the SESSION reports — the office name — must be able to make
   * the header and the home page catch up without a sign-out. Telling the person to sign in again
   * is not an answer: *"הסבר שההתחברות מחזיקה שם ישן אינו תחליף לעדכון התצוגה"*.
   *
   * It is the effect's own read, extracted. A FAILED refresh leaves the session exactly as it was
   * rather than clearing it — losing a working session because a refresh did not answer would
   * evict somebody from the product for changing a display name.
   */
  const refreshSession = useCallback(async (): Promise<void> => {
    if (preSession) return;
    try {
      const me = await callOperation('auth.whoami.read', {});
      setSession(sessionFromWhoami(me));
    } catch {
      // Keep what we have. See above.
    }
  }, [preSession]);

  /*
   * THE COMPANY LIST IS READ FROM THE SERVER TOO. **Phase 2 §6, vertical A.**
   *
   * It is read HERE rather than in `CompanyPickerScreen` because it is not that screen's data: the
   * context bar names the current company on every screen in the product, `TenantCompaniesScreen`
   * and `TenantCompanyAccessScreen` both render it, and 04 I6 requires ONE source for the working
   * context. Four screens each fetching their own list is four answers to one question.
   *
   * ── IT IS SCOPED TO A TENANT SESSION, AND THAT IS NOT AN OPTIMISATION ────────────────────────
   *
   * A platform operator has no companies — GL-POINT's own staff hold no client access, which is the
   * separation the whole SupportAccess model exists to preserve. Asking for the list on a platform
   * surface would be asking the gate a question whose only honest answer is a refusal, and a refusal
   * on every platform page load is noise that trains people to ignore refusals.
   *
   * ── AND AN EMPTY LIST IS NOT A FAILED ONE ────────────────────────────────────────────────────
   *
   * `visibleCompanies` is what a screen renders "you have access to no companies" from. If a failed
   * read collapsed to `[]`, an office whose read was refused would be told they have no clients —
   * so the failure is carried, and `<ServerRead>` on the screens below is what shows it.
   */
  const surface = session?.sessionContext ?? null;
  const readCompanies = useCallback(() => callOperation('companies.list.read', {}), []);
  const companiesRead = useServerData(surface === 'TENANT' ? readCompanies : NO_COMPANIES);

  /*
   * ══ A PERMISSION CHANGE REACHES AN OPEN PAGE. **Owner section 2, 2026-10-28.** ════════════
   *
   *     *"התפריט שלה התעדכן רק לאחר F5 — בדוק כיצד שינוי הרשאות משתקף בחיבור פעיל."*
   *
   * ── WHAT WAS ACTUALLY HAPPENING, WHICH IS LESS BROKEN THAN IT LOOKED ──────────────────
   *
   * The capability list is the SESSION's, read once per page load by the effect above, and the
   * navigation is `visibleDestinations(surface, capabilities)` over it. The server was never
   * stale: the gate reads the grant on every request, so nothing could be DONE with an old menu,
   * and any navigation re-read the session because every link in this shell is a full page load.
   * What stayed stale was the menu of somebody SITTING STILL on one screen while an administrator
   * changed their access in another window — exactly what the two of them were doing.
   *
   * ── SO IT RE-READS WHEN THE PERSON COMES BACK TO THE TAB ──────────────────────────
   *
   * `visibilitychange` and `focus`, because the moment a person's attention returns to a tab is the
   * moment the screen must not be lying to them. NOT a poll: a timer asking `/api/me` forever would
   * cost every open tab in every office a request a minute for a change that happens a few times a
   * year, and `04` §8.2 audits whoami like any other read.
   *
   * BOTH READS, because they answer two halves of one question: the session carries the
   * capabilities the menu is built from, and `companies.list.read` carries the companies a new
   * grant has just added — a refreshed menu over a stale company list would show a destination
   * whose list is still empty.
   *
   * It cannot clear a working session: `refreshSession` keeps what it has when the read fails, and
   * `useServerData`'s own reload carries its failure into `companiesResult` where `<ServerRead>`
   * renders it. A returning tab on a dropped connection therefore shows what it showed before.
   */
  const reloadCompanies = companiesRead.reload;
  useEffect(() => {
    if (preSession) return;
    const catchUp = (): void => {
      if (document.visibilityState !== 'visible') return;
      void refreshSession();
      if (surface === 'TENANT') reloadCompanies();
    };
    document.addEventListener('visibilitychange', catchUp);
    window.addEventListener('focus', catchUp);
    return () => {
      document.removeEventListener('visibilitychange', catchUp);
      window.removeEventListener('focus', catchUp);
    };
  }, [preSession, refreshSession, reloadCompanies, surface]);

  const companies = listOf(companiesRead.result, 'companies');
  const modules = fixturesEnabled() ? fixtureModules : [];
  const periods = fixturesEnabled() ? fixturePeriods : [];

  /*
   * ── THE MFA MANDATE HAS A DOOR, NOT JUST A WALL. **2026-10-25, Owner option 3.** ──────────────
   *
   * The gate now refuses every tenant-scoped route for an administrator inside the MFA floor who
   * holds no second factor. Without this line the person would reach their workspace and watch every
   * panel on it fail with the same denial — a correct server and an unusable product.
   *
   * `mfaEnrolmentRequired` is the SERVER'S answer, read from `/api/me`. This is not a client-side
   * permission check and it cannot grant anything: the gate has already decided, and if this line
   * were deleted the refusals would continue exactly as they are. All it does is put the screen that
   * resolves them in front of the person.
   *
   * IT CANNOT LOOP. `routePatterns.mfaEnrolment` is one of the five `preSessionRoutePatterns`, so on
   * that address the session read is skipped and `session` is null — the condition below is false
   * there by construction, which is also why `preSession` is tested rather than the pathname.
   *
   * The enrolment route is PUBLIC and binds a real cookie at gate stage 1, so the person arrives
   * with their session intact and enrols as themselves rather than against a sign-in challenge.
   */
  if (session !== null && session.mfaEnrolmentRequired && !preSession) {
    return <Navigate to={routes.mfaEnrolment} replace />;
  }

  return (
    <ContextProvider
      refreshSession={refreshSession}
      session={session}
      companies={companies}
      companiesResult={companiesRead.result}
      modules={modules}
      periods={periods}
      navigate={go}
    >
      <Routes>
        {/*
          THE ROOT IS NOT A TENANT ADDRESS. It used to redirect unconditionally to `/companies`,
          so a Platform Owner opening the site landed in a workspace they hold no companies in.
          It now waits for the server's answer and sends each surface to its own home.
        */}
        <Route path="/" element={<RootLanding session={session} read={sessionRead} />} />
        <Route path={routePatterns.welcome} element={<WelcomeScreen />} />
        <Route
          path={routePatterns.signIn}
          element={
            <SignInScreen
              /*
                THE SAME RULE, BY A CLIENT ROUTE. The session cookie exists by now, so the server
                can be asked what it is -- and it is asked, rather than assumed. A failure leaves
                the person on the sign-in screen instead of guessing a surface for them; the 401
                handler above is what routes a genuinely absent session.
              */
              onSignedIn={() => {
                void resolveLandingPath().then(
                  (path) => navigate(path),
                  () => undefined,
                );
              }}
              // A CHALLENGE IS NOT A SESSION. Both of these carry the ref forward and go nowhere
              // near the company list — the principal holds no cookie yet, so routing them into the
              // product would show an empty shell and a denial, and read as a broken sign-in rather
              // than as the second step it is.
              onMfaRequired={(challengeRef) =>
                navigate(`${routes.mfaChallenge}?challenge=${encodeURIComponent(challengeRef)}`)
              }
              onMfaEnrolmentRequired={(challengeRef) =>
                navigate(`${routes.mfaEnrolment}?challenge=${encodeURIComponent(challengeRef)}`)
              }
            />
          }
        />
        {/*
          הבית שלי — the approved customer workspace home, and the TENANT landing. It is a
          different place from the company list: the kit names both, and the product had only the
          second, so arriving at the office dropped a person straight into a picker.
        */}
        <Route path={routePatterns.tenantHome} element={<TenantHomeScreen />} />
        <Route path={routePatterns.companies} element={<CompanyPickerScreen />} />
        <Route path={routePatterns.companyHome} element={<CompanyHomeRoute />} />
        <Route path={routePatterns.moduleHome} element={<ModuleHomeRoute />} />
        <Route path={routePatterns.parentScreen} element={<ParentScreenRoute />} />
        <Route path={routePatterns.blockedDocument} element={<BlockedDocumentRoute />} />
        <Route path={routePatterns.platformConsole} element={<PlatformConsoleScreen />} />

        {/* Control Plane. A static segment out-ranks a parameter, so `/new` never reaches the
            detail screen — and `uid()` would refuse to build such an address anyway. */}
        <Route path={routePatterns.platformTenants} element={<PlatformTenantsScreen />} />
        <Route
          path={routePatterns.platformTenantProvisioning}
          element={<PlatformTenantProvisioningScreen />}
        />
        <Route path={routePatterns.platformSupportGrants} element={<PlatformSupportGrantsScreen />} />
        <Route path={routePatterns.platformBilling} element={<PlatformBillingScreen />} />
        {/* 2026-11-02. מרכז חיוב — the charges of every customer, on a screen of its own. */}
        <Route path={routePatterns.platformCharges} element={<PlatformChargesScreen />} />
        <Route path={routePatterns.platformBillingRun} element={<PlatformBillingRunScreen />} />
        <Route path={routePatterns.platformDunning} element={<PlatformDunningScreen />} />
        <Route path={routePatterns.platformSupportQueue} element={<PlatformSupportQueueScreen />} />
        <Route
          path={routePatterns.platformChangeRequests}
          element={<PlatformChangeRequestsScreen />}
        />
        <Route path={routePatterns.platformCatalogue} element={<PlatformCatalogueScreen />} />
        <Route path={routePatterns.platformPriceBooks} element={<PlatformPriceBooksScreen />} />
        <Route
          path={routePatterns.platformCommercialPolicy}
          element={<PlatformCommercialPolicyScreen />}
        />
        <Route path={routePatterns.platformSignupDoor} element={<PlatformSignupDoorScreen />} />
        <Route path={routePatterns.platformHealth} element={<PlatformHealthScreen />} />
        <Route path={routePatterns.platformPriceBook} element={<PriceBookRoute />} />
        <Route path={routePatterns.platformSecurity} element={<PlatformSecurityScreen />} />
        <Route path={routePatterns.platformTenant} element={<PlatformTenantRoute />} />
        <Route
          path={routePatterns.platformTenantProvisioningResume}
          element={<PlatformTenantProvisioningResumeRoute />}
        />
        <Route
          path={routePatterns.platformTenantSubscription}
          element={<PlatformSubscriptionRoute />}
        />
        {/* Money, deliberately apart from capacity: 08 §8.4 / CF-66. */}
        <Route path={routePatterns.platformTenantCommercial} element={<PlatformCommercialRoute />} />

        {/* Tenant administration. No tenant uid rides in these addresses: the active tenant is a
            property of the session, and a URL that named one would be a second source for it. */}
        <Route path={routePatterns.tenantAdmin} element={<TenantAdminScreen />} />
        <Route path={routePatterns.tenantOrganisation} element={<TenantOrganisationScreen />} />
        <Route
          path={routePatterns.tenantPersonalisation}
          element={<TenantPersonalisationScreen />}
        />
        <Route path={routePatterns.tenantUsers} element={<TenantUsersScreen />} />
        <Route path={routePatterns.tenantWorkItems} element={<TenantWorkItemsScreen />} />
        <Route
          path={routePatterns.tenantReminderRules}
          element={<TenantReminderRulesScreen />}
        />
        <Route path={routePatterns.tenantUserInvitation} element={<TenantUserInvitationScreen />} />
        <Route path={routePatterns.tenantUser} element={<TenantUserRoute />} />
        <Route path={routePatterns.tenantCompanies} element={<TenantCompaniesScreen />} />
        {/* Static before parameter, as above: `/new` and `/authoring` are not uids and never
            reach a detail screen — `uid()` would refuse to build such an address anyway. */}
        <Route
          path={routePatterns.tenantCompanyOnboarding}
          element={<TenantCompanyOnboardingScreen />}
        />
        <Route path={routePatterns.tenantCompany} element={<TenantCompanyRoute />} />
        <Route path={routePatterns.tenantRoles} element={<TenantRolesScreen />} />
        <Route path={routePatterns.tenantRoleAuthoring} element={<TenantRoleAuthoringScreen />} />
        <Route path={routePatterns.tenantCompanyAccess} element={<TenantCompanyAccessScreen />} />
        <Route path={routePatterns.tenantSubscription} element={<TenantSubscriptionScreen />} />
        <Route path={routePatterns.tenantBilling} element={<TenantBillingScreen />} />
        <Route path={routePatterns.tenantSources} element={<SourceConnectionsScreen />} />
        {/* „הפניות שלי” — the read the office surface did not have until 2026-11-04. */}
        <Route path={routePatterns.tenantEnquiries} element={<TenantEnquiriesScreen />} />
        <Route path={routePatterns.tenantSourceConnection} element={<SourceConnectionRoute />} />
        <Route path={routePatterns.tenantSecurity} element={<TenantSecurityScreen />} />

        {/* Identity belongs to the ACCOUNT, so these are reachable from either surface. */}
        <Route
          path={routePatterns.emailChangeConfirm}
          element={<EmailChangeConfirmFromPath />}
        />
        <Route path={routePatterns.personalDetails} element={<PersonalDetailsScreen />} />
        <Route path={routePatterns.personalSecurity} element={<PersonalSecurityScreen />} />
        <Route path={routePatterns.mfaEnrolment} element={<MfaEnrolmentRoute />} />
        <Route path={routePatterns.mfaChallenge} element={<MfaChallengeRoute />} />
        <Route path={routePatterns.invitationAccept} element={<InvitationAcceptRoute />} />
        <Route path={routePatterns.passwordReset} element={<PasswordResetRoute />} />

        {/*
          0032 · THE PUBLIC DOOR. Both are pre-session and neither is inside the shell: a context
          bar and a company switcher drawn for somebody with no account would be furniture for a
          workspace they do not have.
        */}
        <Route path={routePatterns.publicSignup} element={<PublicSignupScreen />} />
        <Route path={routePatterns.signupVerify} element={<SignupVerifyFromPath />} />
        {/*
          0041. PRE-SESSION, beside the two signup addresses and for the same reason: a registering
          stranger coming back from the provider has no account. A SIGNED-IN office replacing its
          card lands here too, which is why the screen's own "what next" link is chosen from the
          server's answer rather than from the shell.
        */}
        <Route path={routePatterns.paymentReturn} element={<PaymentReturnFromPath />} />

        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </ContextProvider>
  );
}
